import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { AnchorResolver } from '@src/editor/models/layout/AnchorResolver';
import {
  capitalizeToken,
  decapitalizeToken,
  findFirstTextToken,
  isPrecededBySentenceEndingPunct,
  isSentenceEndingPunct,
} from '@src/editor/services/capitalization';
import { SceneService } from '@src/services/SceneService';
import { VideoEditorLogger } from '@src/utility/VideoEditorLogger';

import { AbstractCommand } from '../AbstractCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { Scene } from '@src/editor/models/layout/Scene';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { TranscriptClipboardEntry } from '@src/editor/types/transcriptClipboard';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type {
  DeleteInfo,
  ExciseInfo,
  TrimEndInfo,
  TrimStartInfo,
} from '@src/services/SceneService';

export type OperationType =
  | 'delete'
  | 'trimStart'
  | 'trimEnd'
  | 'excise'
  | 'removePunctuation'
  | 'none';

type OperationResult = {
  operation: OperationType;
  removedSegment: ClipTranscriptSegment | null;
  // For excise: IDs of the two resulting elements
  leftElementId?: string;
  rightElementId?: string;
  clipboardEntry?: TranscriptClipboardEntry;
};

/**
 * TranscriptSimpleRemoveCommand – removes a contiguous range of transcript
 * words from a VideoTranscriptElement (specified by their original `initial_index` values).
 * It determines the minimal operation required:
 *   • delete – when removing all words from the element
 *   • trimStart – when removing words from the beginning (respects trimHead flag)
 *   • trimEnd – when removing words from the end (respects trimTail flag)
 *   • excise – when removing words from the middle, cutting out a section
 *
 * The trimHead and trimTail flags control whether parts of the video element
 * outside the removed words range should be kept (intro/outro) or trimmed away.
 *
 * The command focuses on transcript track operations and creates removed
 * clip segments to maintain transcript completeness for undo operations.
 */
export class TranscriptRemoveCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptRemoveCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  private readonly segmentId: string;
  private readonly fromIndex: number;
  private readonly toIndex: number;
  private readonly trimHead: boolean;
  private readonly trimTail: boolean;
  private readonly sceneSnapThreshold: number;
  private readonly _shouldCreateUndoRedoEntry: boolean;

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  private timelineSegmentRemoved: ElementResizeResult | null = null;

  private operationResult: OperationResult | null = null;

  private readonly sceneService = new SceneService();
  private readonly anchorResolver = new AnchorResolver();

  // Scene adjustment info captured BEFORE element modification
  private trimStartInfo: TrimStartInfo | null = null;
  private trimEndInfo: TrimEndInfo | null = null;
  private deleteInfo: DeleteInfo | null = null;
  private exciseInfoPartial: Omit<
    ExciseInfo,
    | 'leftElementId'
    | 'rightElementId'
    | 'leftMediaStart'
    | 'leftMediaEnd'
    | 'rightMediaStart'
    | 'rightMediaEnd'
  > | null = null;

  // Captured scene snapshots for clipboard
  private capturedScenes: Scene[] = [];

  // Pre-captured context for capitalization adjustment after punctuation removal
  private punctRemovalFollowingTokenId: string | null = null;
  private punctRemovalHadSentenceEnder: boolean = false;

  // Centralized cut media times — populated by computeCutMediaTimes(), optionally
  // snapped by adjustCutsToSceneBoundaries(), then consumed by capture/apply methods.
  private cutMediaStart: number | null = null;
  private cutMediaEnd: number | null = null;
  private exciseMediaStart: number | null = null;
  private exciseMediaEnd: number | null = null;

  constructor(
    editor: VideoEditor,
    segmentId: string,
    fromIndex: number,
    toIndex: number,
    trimHead: boolean,
    trimTail: boolean,
    sceneSnapThreshold: number = 0,
    createUndoRedoEntry: boolean = true,
  ) {
    super(editor);
    this.segmentId = segmentId;
    this.fromIndex = Math.min(fromIndex, toIndex);
    this.toIndex = Math.max(fromIndex, toIndex);
    this.trimHead = trimHead;
    this.trimTail = trimTail;
    this.sceneSnapThreshold = Math.max(0, sceneSnapThreshold);
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack()!;
    this.transcriptTrackElements =
      this.transcriptTrack.transcriptSegmentElements;
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;
    if (!track) {
      return false;
    }

    const element = this.lookupElement();
    if (!element) {
      return false;
    }

    const op = this.determineOperation(element);
    if (op === 'none') {
      return false;
    }

    // Pre-compute canonical cut media times once, then optionally snap to scene boundaries.
    this.computeCutMediaTimes(element, op);
    this.adjustCutsToSceneBoundaries(element, op);

    // Capture all scene information while element is in original state
    this.captureOriginalScenesAndCalculations(element, op);

    // Capture capitalization context before operation (for punctuation removal)
    this.captureCapitalizationContext(element, op);

    // Apply the operation (modifies or deletes element)
    this.applyOperation(element, op);

    // Adjust capitalization after removal (e.g., capitalize new first word)
    this.adjustCapitalization(element, op);

    // Apply scene changes using pre-captured information
    this.applyScenesChanges(element.id, op);

    // Recalculate timeline duration and reconcile dependent tracks
    this.recalculateTimeline();
    this.editor.markDirty('transcript_simple_remove');

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Simple remove words (${this.fromIndex}-${this.toIndex}) from segment ${this.segmentId}`;
  }

  getOperationResult(): OperationResult | null {
    return this.operationResult;
  }

  getTimelineSegmentRemoved(): ElementResizeResult | null {
    return this.timelineSegmentRemoved;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private lookupElement(): VideoTranscriptElement | null {
    const element = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(element instanceof VideoTranscriptElement)) {
      return null;
    }
    return element;
  }

  private determineOperation(element: VideoTranscriptElement): OperationType {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return 'none';

    const words = segment.getWords();
    const wordsWithPunctuation = segment.getWordsWithPunctuation();

    // Check if range contains only punctuation first (before checking words.length)
    const tokensInRange = wordsWithPunctuation.filter(
      (_, idx) => idx >= this.fromIndex && idx <= this.toIndex,
    );
    const isPunctuationOnly = tokensInRange.every(
      token => token.type === 'punct',
    );

    if (isPunctuationOnly && tokensInRange.length > 0) {
      return 'removePunctuation';
    }

    // Now check for word operations
    if (words.length === 0) return 'none';

    const wordsToRemove = this.getWordsToRemove(element);
    if (wordsToRemove.length === 0) return 'none';

    const rangeHitsStart =
      this.fromIndex <= wordsWithPunctuation.indexOf(segment.firstWord!) &&
      this.trimHead;
    const rangeHitsEnd =
      this.toIndex >= wordsWithPunctuation.indexOf(segment.lastWord!) &&
      this.trimTail;

    if (rangeHitsStart && rangeHitsEnd) {
      return 'delete';
    }
    if (rangeHitsStart) {
      return 'trimStart';
    }
    if (rangeHitsEnd) {
      return 'trimEnd';
    }

    // Middle section removal (excise)
    return 'excise';
  }

  private applyOperation(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    const track = this.transcriptTrack!;
    switch (op) {
      case 'delete':
        this.operationResult = this.applyDelete(element);
        break;
      case 'trimStart':
        this.operationResult = this.applyTrimStart(element);
        break;
      case 'trimEnd':
        this.operationResult = this.applyTrimEnd(element);
        break;
      case 'excise':
        this.operationResult = this.applyExcise(element, track);
        break;
      case 'removePunctuation':
        this.transcriptTrackManager.removePunctuation(
          element,
          this.fromIndex,
          this.toIndex,
        );
        break;
    }
  }

  private applyDelete(element: VideoTranscriptElement): OperationResult {
    // Remove the element (this will create appropriate removed clip segments)
    const segment = element.getClipTranscriptSegment();
    this.transcriptTrackManager.removeElement(element, TimelineMode.RIPPLE);
    const removedSegment = new ClipTranscriptSegment(
      segment.getWordsWithPunctuation(),
      segment.mediaStart,
      segment.mediaEnd,
    );

    return {
      operation: 'delete',
      removedSegment,
      clipboardEntry: this.createClipboardEntry(element, removedSegment),
    };
  }

  private applyTrimStart(
    element: VideoTranscriptElement,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);

    if (wordsToRemove.length === 0) return null;

    const newTrimStart = this.cutMediaStart!;

    // Trim element (this will create appropriate removed clip segments)
    const removedSegment =
      this.transcriptTrackManager.adjustTranscriptElementMediaStart(
        element,
        newTrimStart,
        TimelineMode.RIPPLE,
        false,
      );
    return {
      operation: 'trimStart',
      removedSegment,
      clipboardEntry: this.createClipboardEntry(element, removedSegment),
    };
  }

  private applyTrimEnd(
    element: VideoTranscriptElement,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);

    if (wordsToRemove.length === 0) return null;

    // Convert the pre-computed (possibly snapped) media cut time to timeline time.
    const newEndTime =
      element.time + (this.cutMediaEnd! - element.getMediaStartTime());

    // Trim element end (this will create appropriate removed clip segments)
    const removedSegment = this.transcriptTrackManager.trimElementEnd(
      element,
      newEndTime,
      TimelineMode.RIPPLE,
      false,
    );
    return {
      operation: 'trimEnd',
      removedSegment,
      clipboardEntry: this.createClipboardEntry(element, removedSegment),
    };
  }

  private applyExcise(
    element: VideoTranscriptElement,
    track: TranscriptTrack,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);
    const firstWordToRemove = wordsToRemove[0];
    const lastWordToRemove = wordsToRemove[wordsToRemove.length - 1];

    if (!firstWordToRemove || !lastWordToRemove) return null;

    // Excise the middle section (this will create appropriate removed clip segments)
    const result = this.transcriptTrackManager.exciseFromTranscriptElement(
      element,
      this.exciseMediaStart!,
      this.exciseMediaEnd!,
      track,
      TimelineMode.RIPPLE,
    );

    if (!result) return null;
    return {
      operation: 'excise',
      removedSegment: result.removedSegment,
      leftElementId: result.firstPart.id,
      rightElementId: result.secondPart.id,
      clipboardEntry: this.createClipboardEntry(element, result.removedSegment),
    };
  }

  private getWordsToRemove(
    element: VideoTranscriptElement,
  ): ClipTranscriptTextToken[] {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return [];

    return segment
      .getWordsWithPunctuation()
      .filter((_, idx) => idx >= this.fromIndex && idx <= this.toIndex)
      .filter(
        (token): token is ClipTranscriptTextToken => token.type === 'text',
      );
  }

  /**
   * Pre-computes the canonical media-time cut boundaries for the operation, so that
   * adjustCutsToSceneBoundaries and the apply/capture methods all share a single source of truth.
   */
  private computeCutMediaTimes(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    if (op === 'delete' || op === 'removePunctuation' || op === 'none') {
      return;
    }

    const segment = element.getClipTranscriptSegment();
    const wordsToRemove = this.getWordsToRemove(element);
    if (wordsToRemove.length === 0) return;

    if (op === 'trimStart') {
      const remainingWords = segment
        .getWordsWithPunctuation()
        .filter((_, idx) => idx > this.toIndex)
        .filter((t): t is ClipTranscriptTextToken => t.type === 'text');
      this.cutMediaStart =
        remainingWords.length > 0
          ? remainingWords[0].media_ts
          : wordsToRemove[wordsToRemove.length - 1].media_end_ts;
    } else if (op === 'trimEnd') {
      this.cutMediaEnd = wordsToRemove[0].media_ts;
    } else if (op === 'excise') {
      this.exciseMediaStart = wordsToRemove[0].media_ts;
      this.exciseMediaEnd =
        wordsToRemove[wordsToRemove.length - 1].media_end_ts;
    }
  }

  /**
   * Snaps pre-computed cut media times to the nearest scene boundary when the cut
   * is within sceneSnapThreshold frames of one.  Only extends the removal — never
   * reduces it — since shrinking the cut cannot eliminate flash frames.
   *
   * Mirrors the CropTimelineCommand.adjustRangesToSceneBoundaries pattern, adapted
   * to work in the element's media-time space rather than timeline time.
   */
  private adjustCutsToSceneBoundaries(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    if (this.sceneSnapThreshold === 0) return;
    if (op === 'delete' || op === 'removePunctuation' || op === 'none') return;

    const timeline = this.editor.timeline;
    const scenes = [...timeline.scenes];
    if (scenes.length === 0) return;

    const frameDuration = 1 / timeline.framerate;
    const snapDistance = this.sceneSnapThreshold * frameDuration;

    const mediaStart = element.getMediaStartTime();
    const mediaEnd = element.getMediaEndTime();

    // Convert timeline-space scene boundaries to media-space, keeping only those
    // that fall strictly inside this element's media range.
    const mediaBoundaries: number[] = [];
    for (const scene of scenes) {
      for (const anchor of [scene.anchorStart, scene.anchorEnd]) {
        try {
          const tl = this.anchorResolver.resolve(anchor, timeline);
          const med = mediaStart + (tl - element.time);
          if (med > mediaStart && med < mediaEnd) {
            mediaBoundaries.push(med);
          }
        } catch {
          // Skip unresolvable anchors — same pattern as CropTimelineCommand
        }
      }
    }

    const uniqueBoundaries = [...new Set(mediaBoundaries)].sort(
      (a, b) => a - b,
    );
    if (uniqueBoundaries.length === 0) return;

    const segment = element.getClipTranscriptSegment();

    /**
     * Finds the closest boundary in the given direction within snapDistance.
     * 'forward'  → only boundaries > target (extends trimStart / excise.end removal)
     * 'backward' → only boundaries < target (extends trimEnd / excise.start removal)
     */
    const snapToClosestExtending = (
      target: number,
      direction: 'forward' | 'backward',
    ): number => {
      let best: number | null = null;
      let bestDist = Infinity;
      for (const b of uniqueBoundaries) {
        const dist = Math.abs(target - b);
        if (dist > snapDistance) continue;
        if (direction === 'forward' && b <= target) continue;
        if (direction === 'backward' && b >= target) continue;
        if (dist < bestDist) {
          best = b;
          bestDist = dist;
        }
      }
      // Intentionally NOT frame-aligned: if the scene boundary is not on a
      // frame grid (e.g. detection-driven scenes carry their source-media
      // precision), rounding to the nearest frame would re-introduce the
      // very mismatch the snap is meant to eliminate.
      return best !== null ? best : target;
    };

    /**
     * Logs when a snap causes findFollowingTextElement to return a different token
     * (meaning an extra word was absorbed into the removed range).  This is intentional
     * but worth surfacing in logs.
     */
    const logIfWordBoundaryShifted = (
      originalCut: number,
      adjustedCut: number,
      cutLabel: string,
    ): void => {
      const origRel = originalCut - segment.mediaStart;
      const adjRel = adjustedCut - segment.mediaStart;
      const origToken = segment.findFollowingTextElement(origRel);
      const adjToken = segment.findFollowingTextElement(adjRel);
      if (origToken?.id !== adjToken?.id) {
        VideoEditorLogger.logTranscriptOperation(
          'transcript_remove_command__scene_snap_absorbed_extra_word',
          {
            cutLabel,
            original: originalCut,
            adjusted: adjustedCut,
            originalFollowingTokenId: origToken?.id ?? null,
            newFollowingTokenId: adjToken?.id ?? null,
          },
        );
      }
    };

    if (op === 'trimStart' && this.cutMediaStart !== null) {
      const original = this.cutMediaStart;
      const snapped = snapToClosestExtending(original, 'forward');
      if (snapped !== original) {
        if (snapped > mediaStart && snapped < mediaEnd) {
          logIfWordBoundaryShifted(original, snapped, 'trimStart');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_remove_command__scene_snap_adjustment',
            {
              op,
              original,
              adjusted: snapped,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.cutMediaStart = snapped;
        }
      }
    } else if (op === 'trimEnd' && this.cutMediaEnd !== null) {
      const original = this.cutMediaEnd;
      const snapped = snapToClosestExtending(original, 'backward');
      if (snapped !== original) {
        if (snapped > mediaStart && snapped < mediaEnd) {
          logIfWordBoundaryShifted(original, snapped, 'trimEnd');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_remove_command__scene_snap_adjustment',
            {
              op,
              original,
              adjusted: snapped,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.cutMediaEnd = snapped;
        }
      }
    } else if (
      op === 'excise' &&
      this.exciseMediaStart !== null &&
      this.exciseMediaEnd !== null
    ) {
      const origStart = this.exciseMediaStart;
      const origEnd = this.exciseMediaEnd;
      const snappedStart = snapToClosestExtending(origStart, 'backward');
      const snappedEnd = snapToClosestExtending(origEnd, 'forward');

      // Both must remain strictly inside the element and start < end.
      const startValid = snappedStart > mediaStart && snappedStart < mediaEnd;
      const endValid = snappedEnd > mediaStart && snappedEnd < mediaEnd;
      const orderValid = snappedStart < snappedEnd;

      if (startValid && endValid && orderValid) {
        if (snappedStart !== origStart) {
          logIfWordBoundaryShifted(origStart, snappedStart, 'excise.start');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_remove_command__scene_snap_adjustment',
            {
              op,
              cut: 'start',
              original: origStart,
              adjusted: snappedStart,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.exciseMediaStart = snappedStart;
        }
        if (snappedEnd !== origEnd) {
          logIfWordBoundaryShifted(origEnd, snappedEnd, 'excise.end');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_remove_command__scene_snap_adjustment',
            {
              op,
              cut: 'end',
              original: origEnd,
              adjusted: snappedEnd,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.exciseMediaEnd = snappedEnd;
        }
      }
      // If validity check fails for either end, leave both at their originals (CropTimelineCommand pattern).
    }
  }

  /**
   * Capture scene information and pre-calculate operation parameters BEFORE element modifications.
   * This ensures we have accurate scene times and operation data before the element state changes.
   */
  private captureOriginalScenesAndCalculations(
    element: VideoTranscriptElement,
    operation: OperationType,
  ): void {
    // Skip scene capture for operations that don't affect scenes
    if (operation === 'removePunctuation' || operation === 'none') {
      return;
    }

    const originalMediaStart = element.getMediaStartTime();
    const originalMediaEnd = element.getMediaEndTime();

    // Calculate the removed part time range
    let removedPartStartTime: number | null = null;
    let removedPartEndTime: number | null = null;

    // Capture trim-specific info — use the pre-computed (possibly snapped) cut times.
    if (operation === 'trimStart') {
      if (this.cutMediaStart !== null) {
        const newMediaStart = this.cutMediaStart;
        this.trimStartInfo = {
          elementId: element.id,
          originalMediaStart,
          originalMediaEnd,
          newMediaStart,
        };

        // Removed part is from element start to newMediaStart
        removedPartStartTime = element.time;
        removedPartEndTime =
          element.time + (newMediaStart - originalMediaStart);
      }
    } else if (operation === 'trimEnd') {
      if (this.cutMediaEnd !== null) {
        const newMediaEnd = this.cutMediaEnd;
        this.trimEndInfo = {
          elementId: element.id,
          originalMediaStart,
          originalMediaEnd,
          newMediaEnd,
        };

        // Removed part is from newMediaEnd to element end
        removedPartStartTime =
          element.time + (newMediaEnd - originalMediaStart);
        removedPartEndTime = element.end_time;
      }
    } else if (operation === 'delete') {
      const prevElement =
        this.transcriptTrack?.getPreviousVideoTranscriptElement(element.id);
      const nextElement = this.transcriptTrack?.getNextVideoTranscriptElement(
        element.id,
      );

      this.deleteInfo = {
        deletedElementId: element.id,
        previousElementId: prevElement?.id ?? null,
        previousElementMediaEnd: prevElement?.getMediaEndTime() ?? null,
        nextElementId: nextElement?.id ?? null,
        nextElementMediaStart: nextElement?.getMediaStartTime() ?? null,
      };

      // Removed part is entire element
      removedPartStartTime = element.time;
      removedPartEndTime = element.end_time;
    } else if (operation === 'excise') {
      // Capture excise info BEFORE element split (will complete AFTER with new element IDs).
      // Use the pre-computed (possibly snapped) excision boundaries.
      if (this.exciseMediaStart !== null && this.exciseMediaEnd !== null) {
        this.exciseInfoPartial = {
          originalElementId: element.id,
          originalMediaStart: element.getMediaStartTime(),
          originalMediaEnd: element.getMediaEndTime(),
          excisionMediaStart: this.exciseMediaStart,
          excisionMediaEnd: this.exciseMediaEnd,
        };

        removedPartStartTime =
          element.time + (this.exciseMediaStart - originalMediaStart);
        removedPartEndTime =
          element.time + (this.exciseMediaEnd - originalMediaStart);
      }
    }

    // Capture overlapping scenes with adjusted anchors
    if (removedPartStartTime !== null && removedPartEndTime !== null) {
      this.capturedScenes = this.sceneService.captureOverlappingScenes(
        this.editor.timeline,
        element,
        removedPartStartTime,
        removedPartEndTime,
        originalMediaStart,
        originalMediaEnd,
      );
    }
  }

  // -------------------------------------------------------------------
  // Capitalization adjustment
  // -------------------------------------------------------------------

  /**
   * Before removing punctuation, record whether any sentence-ending punctuation
   * is being removed and which text token follows it.
   */
  private captureCapitalizationContext(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    if (op !== 'removePunctuation') return;

    const segment = element.getClipTranscriptSegment();
    if (!segment) return;

    const tokens = segment.getWordsWithPunctuation();

    // Check if any token in the removal range is sentence-ending punctuation
    this.punctRemovalHadSentenceEnder = tokens.some(
      (t, idx) =>
        idx >= this.fromIndex &&
        idx <= this.toIndex &&
        t.type === 'punct' &&
        isSentenceEndingPunct(t.value),
    );
    if (!this.punctRemovalHadSentenceEnder) return;

    // Find the first text token after the removed range
    for (let i = this.toIndex + 1; i < tokens.length; i++) {
      if (tokens[i].type === 'text' && tokens[i].state !== 'removed') {
        this.punctRemovalFollowingTokenId = tokens[i].id;
        break;
      }
    }
  }

  /**
   * After applying the removal operation, adjust capitalization:
   * - trimStart: capitalize the new first word of the remaining segment
   * - excise: capitalize the first word of the right part
   * - removePunctuation: decapitalize word that no longer follows sentence-ending punct
   */
  private adjustCapitalization(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    switch (op) {
      case 'trimStart':
        this.capitalizeFirstTokenInSegment(element);
        break;
      case 'excise':
        this.adjustFirstTokenInRightElement();
        break;
      case 'removePunctuation':
        this.decapitalizeAfterPunctRemoval(element);
        break;
    }
  }

  private capitalizeFirstTokenInSegment(element: VideoTranscriptElement): void {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return;

    const tokens = segment.getWordsWithPunctuation();
    const firstText = findFirstTextToken(tokens);
    if (!firstText) return;

    const capitalized = capitalizeToken(firstText);
    if (capitalized === firstText) return;

    const updatedTokens = tokens.map(t =>
      t.id === firstText.id ? capitalized : t,
    );
    element.setClipTranscriptSegment(
      new ClipTranscriptSegment(
        updatedTokens,
        segment.mediaStart,
        segment.mediaEnd,
      ),
    );
  }

  private adjustFirstTokenInRightElement(): void {
    if (
      !this.operationResult?.rightElementId ||
      !this.operationResult?.leftElementId
    ) {
      return;
    }

    const leftEl = this.transcriptTrack?.elements.find(
      el => el.id === this.operationResult!.leftElementId,
    );
    const rightEl = this.transcriptTrack?.elements.find(
      el => el.id === this.operationResult!.rightElementId,
    );
    if (!(leftEl instanceof VideoTranscriptElement)) return;
    if (!(rightEl instanceof VideoTranscriptElement)) return;

    // Check the last non-removed token of the left segment to decide
    // whether the right segment's first word should be capitalized or
    // decapitalized.
    const leftTokens = leftEl
      .getClipTranscriptSegment()
      .getWordsWithPunctuation();
    let leftEndedWithSentencePunct = false;
    for (let i = leftTokens.length - 1; i >= 0; i--) {
      const t = leftTokens[i];
      if (t.state === 'removed') continue;
      if (t.type === 'punct' && isSentenceEndingPunct(t.value)) {
        leftEndedWithSentencePunct = true;
      }
      break;
    }

    if (leftEndedWithSentencePunct) {
      // Left segment ends with a period/!/? → capitalize right's first word
      this.capitalizeFirstTokenInSegment(rightEl);
    } else {
      // Left segment does NOT end with sentence-ending punct →
      // decapitalize right's first word (e.g., "The" → "the")
      this.decapitalizeFirstTokenInSegment(rightEl);
    }
  }

  private decapitalizeFirstTokenInSegment(
    element: VideoTranscriptElement,
  ): void {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return;

    const tokens = segment.getWordsWithPunctuation();
    const firstText = findFirstTextToken(tokens);
    if (!firstText) return;

    const decapitalized = decapitalizeToken(firstText);
    if (decapitalized === firstText) return;

    const updatedTokens = tokens.map(t =>
      t.id === firstText.id ? decapitalized : t,
    );
    element.setClipTranscriptSegment(
      new ClipTranscriptSegment(
        updatedTokens,
        segment.mediaStart,
        segment.mediaEnd,
      ),
    );
  }

  private decapitalizeAfterPunctRemoval(element: VideoTranscriptElement): void {
    if (
      !this.punctRemovalHadSentenceEnder ||
      !this.punctRemovalFollowingTokenId
    ) {
      return;
    }

    const segment = element.getClipTranscriptSegment();
    if (!segment) return;

    const tokens = segment.getWordsWithPunctuation();
    const targetToken = tokens.find(
      t => t.id === this.punctRemovalFollowingTokenId,
    );
    if (!targetToken || targetToken.type !== 'text' || !targetToken.value) {
      return;
    }

    // Don't decapitalize if it's now the first text token (should stay capitalized)
    const firstText = findFirstTextToken(tokens);
    if (firstText && firstText.id === targetToken.id) return;

    // Don't decapitalize if still preceded by sentence-ending punctuation
    if (isPrecededBySentenceEndingPunct(tokens, targetToken.id)) return;

    const decapitalized = decapitalizeToken(targetToken);
    if (decapitalized === targetToken) return;

    const updatedTokens = tokens.map(t =>
      t.id === targetToken.id ? decapitalized : t,
    );
    element.setClipTranscriptSegment(
      new ClipTranscriptSegment(
        updatedTokens,
        segment.mediaStart,
        segment.mediaEnd,
      ),
    );
  }

  private createClipboardEntry(
    element: VideoTranscriptElement,
    removedSegment: ClipTranscriptSegment | null,
  ): TranscriptClipboardEntry | undefined {
    if (!removedSegment) {
      return undefined;
    }

    return {
      segment: removedSegment,
      scenes: this.capturedScenes,
      sourceElementId: element.id,
    };
  }

  private applyScenesChanges(
    targetElementId: string,
    operation: OperationType,
  ): void {
    // Skip for operations that don't affect scenes
    if (operation === 'removePunctuation' || operation === 'none') {
      return;
    }

    const timeline = this.editor.timeline;

    switch (operation) {
      case 'delete':
        // Use new clean scene adjustment method
        if (this.deleteInfo) {
          this.sceneService.adjustScenesAfterDelete(timeline, this.deleteInfo);
        }
        break;

      case 'trimStart':
        // Use new clean scene adjustment method
        if (this.trimStartInfo) {
          this.sceneService.adjustScenesAfterTrimStart(
            timeline,
            this.trimStartInfo,
          );
        }
        break;

      case 'trimEnd':
        // Use new clean scene adjustment method
        if (this.trimEndInfo) {
          this.sceneService.adjustScenesAfterTrimEnd(
            timeline,
            this.trimEndInfo,
          );
        }
        break;

      case 'excise':
        // Build complete excise info and apply scene adjustments
        if (
          this.exciseInfoPartial &&
          this.operationResult?.leftElementId &&
          this.operationResult?.rightElementId
        ) {
          const leftElement = this.transcriptTrack?.elements.find(
            el => el.id === this.operationResult!.leftElementId,
          );
          const rightElement = this.transcriptTrack?.elements.find(
            el => el.id === this.operationResult!.rightElementId,
          );

          if (leftElement && rightElement) {
            const exciseInfo: ExciseInfo = {
              ...this.exciseInfoPartial,
              leftElementId: leftElement.id,
              rightElementId: rightElement.id,
              leftMediaStart: leftElement.getMediaStartTime(),
              leftMediaEnd: leftElement.getMediaEndTime(),
              rightMediaStart: rightElement.getMediaStartTime(),
              rightMediaEnd: rightElement.getMediaEndTime(),
            };
            this.sceneService.adjustScenesAfterExcise(timeline, exciseInfo);
          }
        }
        break;
    }
  }
}
